import React, { useRef, useEffect } from "react";

/**
 * Анимированный фон hero с «дышащими» блобами — порт AnimatedHeroBg
 * из TopPlayersScreen.kt, 1-в-1. Используется в hero-баннерах экранов
 * Топ / Аватар / Друзья / Ещё.
 *
 * Рисует поверх базового цвета 3 круга-блоба (фиолет/синий/белый),
 * которые медленно плавают и пульсируют. Базовый цвет фона задаётся
 * CSS-градиентом на родителе — canvas рисует только блобы.
 */
export default function BlobCanvas({ className }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0;
    let H = 0;

    function resize() {
      const r = canvas.getBoundingClientRect();
      W = r.width;
      H = r.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const TAU = Math.PI * 2;
    const start = performance.now();

    function blob(cx, cy, radius, color) {
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      grad.addColorStop(0, color);
      grad.addColorStop(1, color.replace(/[\d.]+\)$/, "0)"));
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, TAU);
      ctx.fill();
    }

    function draw(now) {
      // t — 12с цикл (0..2π), как в Android
      const elapsed = (now - start) / 1000;
      const t = reduced ? 0 : ((elapsed / 12) % 1) * TAU;

      ctx.clearRect(0, 0, W, H);

      // блоб 1 — фиолет
      blob(
        W * 0.85 + Math.sin(t * 0.5) * 18,
        H * 0.2 + Math.cos(t * 0.6) * 12,
        280 + Math.sin(t * 0.7) * 24,
        "rgba(139,92,246,0.35)"
      );
      // блоб 2 — синий
      blob(
        W * 0.15 + Math.cos(t * 0.4) * 22,
        H * 0.8 + Math.sin(t * 0.55) * 14,
        320 + Math.sin(t * 0.5 + 1.2) * 28,
        "rgba(51,87,255,0.45)"
      );
      // блоб 3 — белый
      blob(
        W * 0.5 + Math.sin(t * 0.3) * 16,
        H * 0.5 + Math.cos(t * 0.45) * 14,
        220 + Math.sin(t * 0.8 + 2.4) * 18,
        "rgba(255,255,255,0.05)"
      );

      if (!reduced) rafRef.current = requestAnimationFrame(draw);
    }

    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
